'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { CompetitionStandingsEmpty, CompetitionStandingsError, CompetitionStandingsLoading, CompetitionStandingsUpcoming } from '@/components/social/competition/competition-boundaries';
import { useCompetitionLabels } from '@/components/social/competition/competition-labels';
import { competitionRankingColumns, competitionRankingMetric, type TradingCompetitionColumn, type TradingCompetitionStandingRow, type TradingCompetitionSummary } from '@/components/social/competition/types';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Badge } from '@/components/ui/app-badge';
import { DataTable } from '@/components/ui/data-table';
import { InformationCircleIcon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { POLL_RING_INTERVAL_MS, POLL_RING_MINUTE_INTERVAL_MS, PollRing } from '@/components/ui/poll-ring';
import { formatCompetitionMoment } from '@/lib/competitions/format';
import { useFormattedCurrency } from '@/lib/currency';
import { SOCIAL_LEADERBOARD_LIST_PANEL_CLASS } from '@/lib/ui/layout';
export type CompetitionStandingsStatus = 'ready' | 'loading' | 'error';
export interface CompetitionStandingsProps {
  competition: TradingCompetitionSummary;
  status: CompetitionStandingsStatus;
  rows: readonly TradingCompetitionStandingRow[];
  /** Timestamp of the last successful standings read — restarts the poll ring. */
  updatedAtMs: number;
  viewerUserId: string | null;
  /** Increment to scroll the viewer's row into view and pulse it. */
  pulseTick: number;
  /** More standings exist behind the loaded page. */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  timeZone: string;
  onRetry: () => void;
  onOpenInfo: () => void;
}
const PRIMARY_COLUMN_CLASS = 'w-[24%] whitespace-nowrap lg:w-[18%]';
const SECONDARY_COLUMN_CLASS = 'hidden w-[13%] whitespace-nowrap lg:table-cell';
function cellValue(row: TradingCompetitionStandingRow, column: TradingCompetitionColumn): string | number {
  return row.values[column.metric] ?? 0;
}
function MetricCell(t0) {
  const $ = _c(14);
  const {
    row,
    column,
    primary
  } = t0;
  let t1;
  if ($[0] !== column || $[1] !== row) {
    t1 = cellValue(row, column);
    $[0] = column;
    $[1] = row;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const value = t1;
  let t2;
  if ($[3] !== primary) {
    t2 = primary ? {} : {
      hideOnMobile: true,
      className: "hidden lg:table-cell"
    };
    $[3] = primary;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const placement = t2;
  if (column.format === "usdSigned") {
    let t3;
    if ($[5] !== placement || $[6] !== value) {
      t3 = <DataTable.NumericCell value={value} format="usdAdaptiveSigned" tone="auto" showThresholdIcon={true} {...placement} />;
      $[5] = placement;
      $[6] = value;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (column.format === "usd") {
    let t3;
    if ($[8] !== placement || $[9] !== value) {
      t3 = <DataTable.NumericCell value={value} format="compact" {...placement} />;
      $[8] = placement;
      $[9] = value;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  let t3;
  if ($[11] !== placement || $[12] !== value) {
    t3 = <DataTable.NumericCell value={value} format="int" {...placement} />;
    $[11] = placement;
    $[12] = value;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}

// The standings are fully locked to the competition metric: no column sorts
// in either direction and headers have no interaction (decided 16 Sep 2026).
export function CompetitionStandings(props) {
  const $ = _c(74);
  const t = useTranslations("competitions.detail");
  const locale = useLocale();
  const labels = useCompetitionLabels();
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    competition
  } = props;
  let t0;
  if ($[0] !== competition || $[1] !== formatD6 || $[2] !== labels || $[3] !== locale || $[4] !== props.hasMore || $[5] !== props.loadingMore || $[6] !== props.onLoadMore || $[7] !== props.onOpenInfo || $[8] !== props.onRetry || $[9] !== props.pulseTick || $[10] !== props.rows || $[11] !== props.status || $[12] !== props.timeZone || $[13] !== props.updatedAtMs || $[14] !== props.viewerUserId || $[15] !== t) {
    const columns = competitionRankingColumns(competition);
    let concluded;
    let explainsCashProfit;
    let rankingMetric;
    let startLabel;
    let t1;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    let upcoming;
    if ($[17] !== competition || $[18] !== labels || $[19] !== locale || $[20] !== props.timeZone || $[21] !== props.updatedAtMs || $[22] !== t) {
      rankingMetric = competitionRankingMetric(competition);
      concluded = competition.phase === "concluded";
      upcoming = competition.phase === "upcoming";
      const metricLabel = labels.metric(rankingMetric);
      let t8;
      if ($[35] !== competition.start || $[36] !== locale || $[37] !== props.timeZone) {
        t8 = formatCompetitionMoment(competition.start, locale, props.timeZone);
        $[35] = competition.start;
        $[36] = locale;
        $[37] = props.timeZone;
        $[38] = t8;
      } else {
        t8 = $[38];
      }
      startLabel = t8;
      explainsCashProfit = competition.rankingSource === "cash_profit";
      t6 = "flex min-w-0 flex-col gap-4";
      t7 = "competition-standings";
      t4 = "flex flex-wrap items-center justify-between gap-3";
      let t9;
      if ($[39] !== concluded || $[40] !== t) {
        t9 = concluded ? t("finalStandings") : t("standings");
        $[39] = concluded;
        $[40] = t;
        $[41] = t9;
      } else {
        t9 = $[41];
      }
      let t10;
      if ($[42] !== competition.rankedParticipants || $[43] !== upcoming) {
        t10 = upcoming ? null : <span className="ml-2 text-body-lg font-medium text-foreground-tertiary">{competition.rankedParticipants}</span>;
        $[42] = competition.rankedParticipants;
        $[43] = upcoming;
        $[44] = t10;
      } else {
        t10 = $[44];
      }
      if ($[45] !== t10 || $[46] !== t9) {
        t5 = <h2 className="text-heading text-foreground-primary">{t9}{t10}</h2>;
        $[45] = t10;
        $[46] = t9;
        $[47] = t5;
      } else {
        t5 = $[47];
      }
      t2 = "flex items-center gap-2 text-caption text-foreground-tertiary";
      if ($[48] !== competition.phase || $[49] !== competition.rankingSource || $[50] !== props.updatedAtMs || $[51] !== t) {
        t3 = competition.phase === "live" ? <><PollRing resetKey={props.updatedAtMs} intervalMs={competition.rankingSource === "board" ? POLL_RING_INTERVAL_MS : POLL_RING_MINUTE_INTERVAL_MS} label={competition.rankingSource === "board" ? t("refreshesBoard") : t("refreshesAdjusted")} /><span aria-hidden="true">·</span></> : null;
        $[48] = competition.phase;
        $[49] = competition.rankingSource;
        $[50] = props.updatedAtMs;
        $[51] = t;
        $[52] = t3;
      } else {
        t3 = $[52];
      }
      t1 = t("rankedBy", {
        metric: metricLabel
      });
      $[17] = competition;
      $[18] = labels;
      $[19] = locale;
      $[20] = props.timeZone;
      $[21] = props.updatedAtMs;
      $[22] = t;
      $[23] = concluded;
      $[24] = explainsCashProfit;
      $[25] = rankingMetric;
      $[26] = startLabel;
      $[27] = t1;
      $[28] = t2;
      $[29] = t3;
      $[30] = t4;
      $[31] = t5;
      $[32] = t6;
      $[33] = t7;
      $[34] = upcoming;
    } else {
      concluded = $[23];
      explainsCashProfit = $[24];
      rankingMetric = $[25];
      startLabel = $[26];
      t1 = $[27];
      t2 = $[28];
      t3 = $[29];
      t4 = $[30];
      t5 = $[31];
      t6 = $[32];
      t7 = $[33];
      upcoming = $[34];
    }
    let t8;
    if ($[53] !== t1) {
      t8 = <span>{t1}</span>;
      $[53] = t1;
      $[54] = t8;
    } else {
      t8 = $[54];
    }
    let t9;
    if ($[55] !== explainsCashProfit || $[56] !== t) {
      t9 = explainsCashProfit ? t("howCashProfitWorks") : t("howItWorks");
      $[55] = explainsCashProfit;
      $[56] = t;
      $[57] = t9;
    } else {
      t9 = $[57];
    }
    let t10;
    if ($[58] !== explainsCashProfit || $[59] !== t) {
      t10 = explainsCashProfit ? t("howItWorksShort") : null;
      $[58] = explainsCashProfit;
      $[59] = t;
      $[60] = t10;
    } else {
      t10 = $[60];
    }
    let t11;
    if ($[61] !== props.onOpenInfo || $[62] !== t10 || $[63] !== t9) {
      t11 = <AnimatedButton variant="tertiary" size="xs" prefix={InformationCircleIcon} aria-label={t9} onClick={props.onOpenInfo}>{t10}</AnimatedButton>;
      $[61] = props.onOpenInfo;
      $[62] = t10;
      $[63] = t9;
      $[64] = t11;
    } else {
      t11 = $[64];
    }
    let t12;
    if ($[65] !== t11 || $[66] !== t2 || $[67] !== t3 || $[68] !== t8) {
      t12 = <div className={t2}>{t3}{t8}{t11}</div>;
      $[65] = t11;
      $[66] = t2;
      $[67] = t3;
      $[68] = t8;
      $[69] = t12;
    } else {
      t12 = $[69];
    }
    let t13;
    if ($[70] !== t12 || $[71] !== t4 || $[72] !== t5) {
      t13 = <div className={t4}>{t5}{t12}</div>;
      $[70] = t12;
      $[71] = t4;
      $[72] = t5;
      $[73] = t13;
    } else {
      t13 = $[73];
    }
    t0 = <section className={t6} data-testid={t7}>{t13}{upcoming ? <CompetitionStandingsUpcoming startLabel={startLabel} onOpenInfo={props.onOpenInfo} /> : props.status === "loading" ? <CompetitionStandingsLoading columnCount={columns.length} /> : props.status === "error" ? <CompetitionStandingsError onRetry={props.onRetry} /> : props.rows.length === 0 ? <CompetitionStandingsEmpty onOpenInfo={props.onOpenInfo} /> : <Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="competition-standings-panel"><DataTable panel="borderless" desktopRowChrome="feed" entries={props.rows} sortBy={rankingMetric} sortOrder="desc" onSortChange={_temp} pulseTick={props.pulseTick} data-testid="competition-standings-table"><DataTable.Header><DataTable.RankColumn /><DataTable.IdentityColumn header={labels.board(competition.board)} />{columns.map((column, index) => <DataTable.Column key={column.metric} header={labels.metric(column.metric)} align="right" className={index === 0 ? PRIMARY_COLUMN_CLASS : SECONDARY_COLUMN_CLASS} hideOnMobile={index !== 0} />)}</DataTable.Header><DataTable.Body>{row => {
              const isViewer = props.viewerUserId !== null && row.user.userId === props.viewerUserId;
              const userId = row.user.userId ?? "";
              const link = userId.length > 0 ? {
                href: `/portfolio/${userId}`
              } : {};
              const winningsD6 = row.prizeD6 !== null && row.prizeD6 !== "0" ? row.prizeD6 : null;
              const winningsBadge = winningsD6 === null ? undefined : <Badge variant="secondary" className="shrink-0 text-micro" data-testid="competition-winnings-badge" {...concluded ? {} : {
                title: t("predictedWinningsLabel")
              }}>{concluded ? t("wonPrize", {
                  amount: formatD6(winningsD6, "compactTight")
                }) : t("predictedWinnings", {
                  amount: formatD6(winningsD6, "compactTight")
                })}</Badge>;
              return <DataTable.Row key={userIdentityKey(row.user, `anonymous-rank-${row.rank}`)} isCurrentUser={isViewer} ariaLabel={t("viewProfile")} {...link}><DataTable.RankCell value={row.rank} unrankedLabel={t("ineligible")} isCurrentUser={isViewer} /><DataTable.IdentityCell user={row.user} showYouBadge={isViewer} badge={winningsBadge} />{columns.map((column_0, index_0) => <MetricCell key={column_0.metric} row={row} column={column_0} primary={index_0 === 0} />)}</DataTable.Row>;
            }}</DataTable.Body></DataTable>{props.hasMore ? <div className="flex justify-center p-4"><AnimatedButton variant="secondary" size="sm" disabled={props.loadingMore} onClick={props.onLoadMore} data-testid="competition-standings-more">{props.loadingMore ? t("loadingMore") : t("showMore")}</AnimatedButton></div> : null}</Panel>}</section>;
    $[0] = competition;
    $[1] = formatD6;
    $[2] = labels;
    $[3] = locale;
    $[4] = props.hasMore;
    $[5] = props.loadingMore;
    $[6] = props.onLoadMore;
    $[7] = props.onOpenInfo;
    $[8] = props.onRetry;
    $[9] = props.pulseTick;
    $[10] = props.rows;
    $[11] = props.status;
    $[12] = props.timeZone;
    $[13] = props.updatedAtMs;
    $[14] = props.viewerUserId;
    $[15] = t;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  return t0;
}
function _temp() {}